import { ref, computed } from 'vue'
import type { RouteLocationNormalized } from 'vue-router'
import * as Sentry from '@sentry/vue'
import { buildId } from './env'

const POLL_INTERVAL_MS = 5 * 60 * 1000
const CHUNK_ERROR_RELOAD_KEY = 'chunk-error-reload'
const CRITICAL_ROUTE_NAMES = new Set(['session', 'review'])

const isUpdateAvailable = ref(false)
const isChunkError = ref(false)
const isCriticalRoute = ref(false)

const shouldShowBanner = computed(
  () => (isUpdateAvailable.value || isChunkError.value) && !isCriticalRoute.value,
)

async function checkVersion(): Promise<void> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`)
    const data = await res.json()
    if (data.buildId !== buildId) {
      isUpdateAvailable.value = true
    } else {
      sessionStorage.removeItem(CHUNK_ERROR_RELOAD_KEY)
    }
  } catch (err) {
    console.warn('[version] Failed to fetch version.json:', err)
  }
}

async function onRouteChange(route: Pick<RouteLocationNormalized, 'name'>): Promise<void> {
  isCriticalRoute.value = CRITICAL_ROUTE_NAMES.has(route.name as string)
  if (!import.meta.env.DEV) await checkVersion()
}

async function onVisibilityChange(): Promise<void> {
  if (!import.meta.env.DEV && document.visibilityState === 'visible') {
    await checkVersion()
  }
}

function onChunkError(): void {
  isChunkError.value = true
  Sentry.captureException(new Error('ChunkLoadError: failed to load dynamic import'))

  if (isCriticalRoute.value) return
  if (sessionStorage.getItem(CHUNK_ERROR_RELOAD_KEY)) return
  sessionStorage.setItem(CHUNK_ERROR_RELOAD_KEY, '1')
  setTimeout(() => window.location.reload(), 3_000)
}

function startPolling(): () => void {
  if (import.meta.env.DEV) return () => {}
  const id = setInterval(checkVersion, POLL_INTERVAL_MS)
  return () => clearInterval(id)
}

export function useVersionChecker() {
  return {
    isUpdateAvailable,
    isChunkError,
    isCriticalRoute,
    shouldShowBanner,
    checkVersion,
    onRouteChange,
    onVisibilityChange,
    onChunkError,
    startPolling,
  }
}
